import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { addLocaleData, getStrapiMedia } from "../../utils/utils";
import "./Header.scss";
import { HeaderComponent_Plain } from "../../types/components/global/HeaderComponent.ts";
import { LinkComponent_Plain } from "../../types/components/shared/LinkComponent.ts";
import { LinWithIconComponent_Plain } from "../../types/components/shared/LinWithIconComponent.ts";
import { useTranslation } from "react-i18next";
import { SublinkComponent_Plain } from "../../types/components/shared/SublinkComponent.ts";
interface HeaderProps {
  data: HeaderComponent_Plain;
  locale: string;
  onMenuStateChange?: (isOpen: boolean) => void;
}
const Header = ({ data, locale, onMenuStateChange }: HeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false); // Добавлено состояние для подменю
  const [selectedLocation] = useState(
    locale === "uk"
      ? "UKRAINIAN"
      : locale === "ro"
        ? "ROMANIAN"
        : locale === "pl"
          ? "POLISH"
          : locale === "hr"
            ? "CROATIAN"
            : "ENGLISH",
  );
  const [isMobile, setIsMobile] = useState(false);
  const [isContactBtnVisible, setIsContactBtnVisible] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const navContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setIsMobile(window.innerWidth <= 767);
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    if (onMenuStateChange) {
      onMenuStateChange(isMenuOpen);
    }
  }, [isMenuOpen, onMenuStateChange]);
  useEffect(() => {
    if (locale === "uk") {
      document.body.classList.add("ua-ver");
      document.body.classList.remove("en-ver");
    } else {
      document.body.classList.remove("ua-ver");
      document.body.classList.add("en-ver");
    }
  }, [locale]);
  const scrollToElement = (hash: string) => {
    const elementId = hash.replace("#", "");
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          scrollToElement(hash);
        }, 1000);
      }
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const header = headerRef.current;
      if (header) {
        if (currentScrollY < lastScrollY && currentScrollY > 0) {
          header.classList.add("fixed-header");
        } else {
          header.classList.remove("fixed-header");
        }
      }
      lastScrollY = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const handleScrollForButton = () => {
      const header = headerRef.current;
      const menuButton = menuButtonRef.current;
      const currentScrollY = window.scrollY;
      if (header && menuButton) {
        const hasWithNote = header.classList.contains("with-note");
        if (hasWithNote) {
          if (currentScrollY > 70) {
            menuButton.classList.add("fixed-btn");
          } else {
            menuButton.classList.remove("fixed-btn");
          }
        }
      }
    };
    window.addEventListener("scroll", handleScrollForButton);
    return () => {
      window.removeEventListener("scroll", handleScrollForButton);
    };
  }, []);
  useEffect(() => {
    const handleContactBtnScroll = () => {
      const threshold = window.innerHeight * 0.9;
      setIsContactBtnVisible(window.scrollY > threshold);
    };
    window.addEventListener("scroll", handleContactBtnScroll);
    return () => {
      window.removeEventListener("scroll", handleContactBtnScroll);
    };
  }, []);
  const handleMenuItemClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: string,
  ) => {
    e.preventDefault();
    const url = new URL(link, window.location.origin);
    const hash = url.hash;
    if (hash) {
      const normalize = (p: string) => p.replace(/\/$/, "") || "/";
      const targetPath = normalize(url.pathname);
      const currentPath = normalize(window.location.pathname);
      const isSamePage =
        targetPath === currentPath ||
        (currentPath === "/" && targetPath === "/en");
      if (isSamePage) {
        setIsMenuOpen(false);
        // Use React Router navigate to trigger Lenis scroll in App.tsx
        if (routerLocation.hash === hash) {
          // Hash already set — clear and re-set to force re-trigger
          navigate(currentPath, { replace: true });
          setTimeout(() => navigate(currentPath + hash, { replace: true }), 10);
        } else {
          navigate(currentPath + hash, { replace: true });
        }
      } else {
        window.location.href = link;
      }
    } else {
      window.location.href = link;
    }
  };
  // Обработчик для переключения подменю
  const handleSubmenuToggle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsSubmenuOpen(!isSubmenuOpen);
  };
  const menuItems = (data.menu || []).map(
    (item: LinkComponent_Plain, i: number) => {
      const rawLink = item.link ?? "";
      const link = addLocaleData(rawLink, locale);
      if (!link) return null;
      return (
        <li
          key={i}
          className={
            i == 0 ? `with-subnav ${isSubmenuOpen ? "opened" : ""}` : ""
          }
        >
          <a
            href={link}
            target={item.target}
            onClick={(e) => {
              if (i === 0 && !link.startsWith("#")) {
                handleSubmenuToggle(e);
              } else {
                handleMenuItemClick(e, link);
              }
            }}
          >
            {item.title}
            {item.label ? <span className="label">{item.label}</span> : ""}
            {item.icon ? (
              <span className="menu-icon">
                <img
                  src={getStrapiMedia(item.icon.url)}
                  width="50"
                  alt={item.title}
                />
              </span>
            ) : (
              ""
            )}
          </a>
          {i == 0 ? (
            <ul className={`subnav ${isSubmenuOpen ? "expanded" : ""}`}>
              {(data.productSubmenu || []).map(
                (subitem: SublinkComponent_Plain, i2: number) => {
                  let subLink = addLocaleData(subitem.link ?? "", locale);
                  if (!subLink) {
                    subLink = "";
                  }
                  return (
                    <li key={i2}>
                      <a
                        href={subLink}
                        onClick={(e) => handleMenuItemClick(e, subLink)}
                      >
                        {subitem.title}
                      </a>
                    </li>
                  );
                },
              )}
            </ul>
          ) : (
            ""
          )}
        </li>
      );
    },
  );
  const socialItems = (data.social || []).map(
    (item: LinWithIconComponent_Plain, i: number) => (
      <li key={i}>
        <a href={item.link} target={item.target}>
          {item.icon ? (
            <img
              src={getStrapiMedia(item.icon.url)}
              width="50"
              alt={item.title}
            />
          ) : (
            ""
          )}
        </a>
      </li>
    ),
  );
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    // Сбрасываем состояние подменю при закрытии главного меню
    if (isMenuOpen) {
      setIsSubmenuOpen(false);
    }
  };
  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setIsSubmenuOpen(false); // Сбрасываем состояние подменю
  };
  const locations = [
    {
      name: "ENGLISH",
      shortName: "EN",
      value: "ENGLISH",
      link: "/",
    },
    {
      name: "ROMANIAN",
      shortName: "RO",
      value: "ROMANIAN",
      link: "/ro",
    },
    {
      name: "POLISH",
      shortName: "PL",
      value: "POLISH",
      link: "/pl",
    },
    {
      name: "CROATIAN",
      shortName: "HR",
      value: "CROATIAN",
      link: "/hr",
    },
    {
      name: "UKRAINIAN",
      shortName: "UA",
      value: "UKRAINIAN",
      link: "/ua",
    },
  ];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // Hydration-safe: initial render must match SSR.
  // localStorage reading happens in an effect after mount.
  const [isNoteVisible, setIsNoteVisible] = useState(true);
  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    if (localStorage.getItem("headerNoteClosed") === "true") {
      setIsNoteVisible(false);
    }
  }, []);
  const handleCloseNote = () => {
    setIsNoteVisible(false);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("headerNoteClosed", "true");
    }
  };
  const [isNoteDisplayed, setIsNoteDisplayed] = useState(false);
  useEffect(() => {
    if (isNoteVisible) {
      const timer = setTimeout(() => {
        setIsNoteDisplayed(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isNoteVisible]);
  // Обработчик прокрутки колесиком мыши для меню
  const handleMenuWheel = (e: WheelEvent) => {
    if (navContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = navContentRef.current;
      const isScrollingUp = e.deltaY < 0;
      const isScrollingDown = e.deltaY > 0;
      // Проверяем, можем ли мы прокручивать в нужном направлении
      const canScrollUp = scrollTop > 0;
      const canScrollDown = scrollTop < scrollHeight - clientHeight;
      if (
        (isScrollingUp && canScrollUp) ||
        (isScrollingDown && canScrollDown)
      ) {
        // Позволяем стандартному поведению прокрутки
        e.stopPropagation(); // Предотвращаем всплытие события
        return;
      } else {
        // Блокируем событие, если достигли границ
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  // Обработчик для блокировки прокрутки body, но разрешения прокрутки меню
  const handleBodyWheel = (e: WheelEvent) => {
    // Проверяем, происходит ли событие внутри меню
    if (
      navContentRef.current &&
      navContentRef.current.contains(e.target as Node)
    ) {
      // Если да, то позволяем обработчику меню обработать событие
      return;
    }
    // Если нет, то блокируем прокрутку body
    e.preventDefault();
  };
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const navContentEl = navContentRef.current;
    if (isMenuOpen) {
      // Сохраняем текущую позицию прокрутки
      const scrollTop = window.pageYOffset || html.scrollTop;
      // Блокируем прокрутку основной страницы только для мобильных
      const isMobile = window.innerWidth <= 767;
      if (isMobile) {
        body.style.position = "fixed";
        body.style.top = `-${scrollTop}px`;
        body.style.left = "0";
        body.style.right = "0";
        body.style.overflow = "hidden";
        body.style.touchAction = "none";
      }
      // Сохраняем позицию для восстановления
      body.setAttribute("data-scroll-lock", scrollTop.toString());
      // Добавляем обработчики для контроля прокрутки
      if (navContentEl) {
        navContentEl.addEventListener("wheel", handleMenuWheel, {
          passive: false,
        });
      }
      // Для десктопа блокируем прокрутку body через wheel события
      if (!isMobile) {
        document.addEventListener("wheel", handleBodyWheel, { passive: false });
      }
    } else {
      // Восстанавливаем прокрутку
      const scrollTop = body.getAttribute("data-scroll-lock") || "0";
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      body.style.touchAction = "";
      body.removeAttribute("data-scroll-lock");
      // Восстанавливаем позицию прокрутки
      window.scrollTo(0, parseInt(scrollTop));
      // Убираем обработчики событий прокрутки
      if (navContentEl) {
        navContentEl.removeEventListener("wheel", handleMenuWheel);
      }
      document.removeEventListener("wheel", handleBodyWheel);
    }
    // Cleanup функция
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.overflow = "";
      body.style.touchAction = "";
      body.removeAttribute("data-scroll-lock");
      if (navContentEl) {
        navContentEl.removeEventListener("wheel", handleMenuWheel);
      }
      document.removeEventListener("wheel", handleBodyWheel);
    };
  }, [isMenuOpen]);
  const shortText =
    locale === "uk"
      ? "UA"
      : locale === "ro"
        ? "RO"
        : locale === "pl"
          ? "PL"
          : locale === "hr"
            ? "HR"
            : "EN";
  return (
    <header className={isNoteVisible ? "" : ""} ref={headerRef}>
      {isNoteVisible && (
        <>
          {locale === "uk" ? (
            <div className={`header-note ${isNoteDisplayed ? "visible" : ""}`}>
              <div className="wrapper">
                <div className="header-note__name">
                  Сайт працює в тестовому режимі, і ми активно вдосконалюємо наш
                  функціонал.
                </div>
                <div className="header-note__text">
                  Знайшли помилку чи маєте ідею? Поділіться з нами через{" "}
                  <a
                    href="mailto:HorielovaMV@d.trading"
                    className="btn-contact"
                  >
                    форму зворотнього зв'язку
                  </a>
                  .
                </div>
                <div className="header-note__close" onClick={handleCloseNote}>
                  <span></span>
                </div>
              </div>
            </div>
          ) : (
            <div className={`header-note ${isNoteDisplayed ? "visible" : ""}`}>
              <div className="wrapper">
                <div className="header-note__name">
                  The website is currently in test mode, and we are actively
                  improving our features.
                </div>
                <div className="header-note__text">
                  Found a mistake or have an idea? Share it with us using the{" "}
                  <a
                    href="mailto:HorielovaMV@d.trading"
                    className="btn-contact"
                  >
                    feedback form
                  </a>
                  .
                </div>
                <div className="header-note__close" onClick={handleCloseNote}>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <div className="wrapper">
        <div className="header-logo">
          <a href={locale === "en" ? "/" : addLocaleData("/", locale)}>
            <img width="104" height="13" src={getStrapiMedia(data.logo.url)} />
          </a>
        </div>
        <div className="header-right">
          <div className="header-select header-select_without-label">
            <div className="header-select__wrap">
              <div
                className={`message-to__select ${isDropdownOpen ? "active-select" : ""}`}
                ref={selectRef}
              >
                <div
                  className="message-to__select-title"
                  ref={titleRef}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {isMobile ? shortText : selectedLocation}
                </div>
                <div className="message-to__select-dropdown">
                  <ul ref={dropdownRef}>
                    {locations.map((loc, i) => (
                      <li
                        key={i}
                        data-value={loc.value}
                        className={
                          selectedLocation === loc.name ? "active" : ""
                        }
                      >
                        <a
                          href={loc.link}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsDropdownOpen(false);
                            window.location.href = loc.link;
                          }}
                        >
                          {isMobile ? loc.shortName : loc.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          {isContactBtnVisible && (
            <a href="https://d.trading/#contacts" className="btn-contact-fixed">
              {t("Contact us")}
            </a>
          )}
          <div
            className="btn-menu"
            onClick={handleMenuToggle}
            ref={menuButtonRef}
          >
            {t("Menu")}
          </div>
        </div>
      </div>
      <div className={`main-nav ${isMenuOpen ? "opened" : ""}`}>
        <div className="box-nav__mask" onClick={handleCloseMenu}></div>
        <nav className="box-nav">
          <div className="btn-close-nav" onClick={handleCloseMenu}>
            {t("Close")}
          </div>
          <div className="box-nav-content" ref={navContentRef}>
            <div className="header-menu">
              <ul>{menuItems}</ul>
            </div>
            {locale === "uk" ? (
              <div className="cabinet">
                <a
                  href={"https://cabinet.d.trading/"}
                  target={"_blank"}
                  className="btn-black"
                >
                  {t("Personal account")}
                </a>
              </div>
            ) : (
              ""
            )}
            <div className="header-social">
              <ul>{socialItems}</ul>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};
export default Header;
